import { useEffect, useState } from 'react';
import { IPageReport } from '../redux/builderStore';
import { isNullOrUndefined } from '../utils/object';
import { getTokenFor } from '../utils/arcgis';
import { isSameDomain, findCoreLayer, findStandardFields } from '../utils/arcgis-network';
import { findIndicatorRefs } from '../utils/report-migrate-plus';
import { fetchReportDataFromBrowser } from '../utils/storage';
// @ts-expect-error - data-catalog-js-api is not typed (yet)
import { ArcGISPortal, ReportDataSource, JsonFileDataSource } from 'data-catalog-js-api';
import { NamedItem } from '../pages/commonTypes';
import esriId from '@arcgis/core/identity/IdentityManager';

export type ReportViewerDataSourceResponse = {
    /** The ReportDataSource object that loads data */
    dataSource: any;
    /** A specific data catalog source, if one exists */
    catalogSource: any;
    /** The currently active, or default, or first feature in the core set */
    activeFeature: NamedItem[] | null;
    /** Any error that occurs while loading data */
    error?: { [id: string]: any };
    /** Any warning that occurs while loading data */
    warning?: { [id: string]: any };
    /** Current loading status */
    loading: boolean;
    /** If using a layer view, the core set of feature IDs that should be used to subset any other queries */
    maxFeatureFilter?: any[] | null;
    /** If overriding a layer, the core set of relationships from that layer */
    coreRelationships?: any[] | null;
    /** If overriding a layer, the name from that layer */
    coreName?: string;
};

export const useReportViewerDataSource = ({
    reportItem,
    oldReportItem,
    featureIds,
    oldFeatureIds,
    portalUrl,
    staticUrl,
    abortController,
    mode = 'single',
    dataMode = 'live',
    token,
    reportQueryParams = {},
    reportLayerIdOverride
}: {
    reportItem?: IPageReport;
    oldReportItem?: IPageReport;
    featureIds?: string;
    oldFeatureIds?: string;
    portalUrl: string;
    staticUrl: string;
    abortController?: AbortController;
    mode?: 'aggregate' | 'single';
    dataMode?: 'live' | 'local' | 'static';
    token?: string;
    reportQueryParams?: { [id: string]: any };
    reportLayerIdOverride?: string;
}): ReportViewerDataSourceResponse => {
    const [loading, setLoading] = useState<boolean>(true);
    const [dataProvider, setDataProvider] = useState<any>();
    const [catalogSource, setCatalogSource] = useState<any>();
    const [activeFeature, setActiveFeature] = useState<NamedItem[] | null>(null);
    const [error, setError] = useState<{ [id: string]: any }>();
    const [warning, setWarning] = useState<{ [id: string]: any }>();
    const [maxFeatureFilter, setMaxFeatureFilter] = useState<any[] | null>(null);
    const [coreRelationships, setCoreRelationships] = useState<any[] | null>(null);
    const [coreName, setCoreName] = useState<string>();

    const oldId: string = oldReportItem !== undefined && oldReportItem.info !== undefined ? oldReportItem.info.id : '',
        reportExists: boolean = reportItem !== undefined && reportItem !== null && reportItem.info !== undefined,
        reportId = reportItem?.info?.id,
        featuresHaveChanged = oldFeatureIds !== featureIds,
        reportHasChanged: boolean =
            reportExists && (oldReportItem === undefined || oldId !== reportId || featuresHaveChanged);

    useEffect(() => {
        if (!reportHasChanged || !reportExists || featureIds === undefined) {
            return;
        }
        if (reportItem !== undefined) {
            const { report, info, webmap } = reportItem ?? {};
            const stamp = new Date().getTime().toFixed(0);
            // Got the report, now see if there are data sources to populate the on-screen items...
            if (report !== undefined) {
                (async () => {
                    setLoading(true);
                    try {
                        let reportDataSource: ReportDataSource | JsonFileDataSource,
                            catalogSrc,
                            f: any,
                            adjustedByQueryString = false;
                        if (dataMode === 'local') {
                            reportDataSource = await fetchReportDataFromBrowser(info.id, featureIds, token);
                            f = [
                                {
                                    id: featureIds,
                                    name: featureIds
                                }
                            ];
                            if (!isNullOrUndefined(report.data) && !isNullOrUndefined(report.data.sources)) {
                                catalogSrc = report.data.sources.find((s: any) => s.type === 'DataCatalog');
                            }
                        } else if (dataMode === 'static') {
                            const coreFeatureUrl = `${staticUrl.replace(/\/$/, '')}/${info.id}/${(
                                info.pattern || '#FID.json'
                            ).replace('#FID', featureIds)}`;
                            reportDataSource = new JsonFileDataSource(coreFeatureUrl, token, 0);
                            f = await reportDataSource.init().then(() => {
                                return [
                                    {
                                        id: featureIds,
                                        name: featureIds
                                    }
                                ];
                            });
                            if (!isNullOrUndefined(report.data) && !isNullOrUndefined(report.data.sources)) {
                                catalogSrc = report.data.sources.find((s: any) => s.type === 'DataCatalog');
                            }
                        } else if (!isNullOrUndefined(report.data) && !isNullOrUndefined(report.data.sources)) {
                            let dataSourcesPromise: Promise<any[]> = Promise.resolve(report.data.sources);
                            const overLayerId =
                                    reportQueryParams['layer'] !== undefined
                                        ? reportQueryParams['layer']
                                        : reportLayerIdOverride !== undefined && reportLayerIdOverride !== null
                                          ? reportLayerIdOverride
                                          : '',
                                overCatalogId =
                                    reportQueryParams['catalog'] !== undefined ? reportQueryParams['catalog'] : '';
                            // Override core data source?
                            if (
                                report.data.allowCoreOverride !== undefined &&
                                report.data.allowCoreOverride !== null &&
                                report.data.allowCoreOverride === true &&
                                overLayerId !== null &&
                                overLayerId !== ''
                            ) {
                                const ovDataSources: any[] = [...(report.data.sources as any[])];
                                // GUID => an item inside ArcGIS Online
                                if (
                                    /^[0-9a-fA-F]{8}[0-9a-fA-F]{4}[0-9a-fA-F]{4}[0-9a-fA-F]{4}[0-9a-fA-F]{12}$/.test(
                                        overLayerId
                                    )
                                ) {
                                    dataSourcesPromise = ArcGISPortal.getItemDescription(
                                        overLayerId,
                                        { f: 'json', token: token },
                                        portalUrl
                                    ).then((layerItem: any) => {
                                        ovDataSources[0].url = layerItem.url;
                                        return ovDataSources;
                                    });
                                    adjustedByQueryString = true;
                                }
                                // https: => a raw URL
                                else if (/^http|https:\/\/.*$/.test(overLayerId)) {
                                    dataSourcesPromise = new Promise((resolve, reject) => {
                                        ovDataSources[0].url = overLayerId;
                                        resolve(ovDataSources);
                                    });
                                    adjustedByQueryString = true;
                                }
                                // Otherwise must be an ID inside a catalog... which imposes more requests...
                                else if (overCatalogId !== null && overCatalogId !== '') {
                                    dataSourcesPromise = findCoreLayer(
                                        overCatalogId,
                                        overLayerId,
                                        portalUrl,
                                        token
                                    ).then((urls: string[]) => {
                                        const [geoUrl, tableUrl] = urls;
                                        if (geoUrl !== undefined && geoUrl !== null) {
                                            ovDataSources[0].url = geoUrl;
                                            const tempCatalogSrc = ovDataSources.find((s) => s.type === 'DataCatalog');
                                            if (tempCatalogSrc !== undefined) tempCatalogSrc.url = tableUrl;
                                        }
                                        return ovDataSources;
                                    });
                                    adjustedByQueryString = true;
                                }
                            }
                            //dataSourcesPromise.then((reportDataSources: any) => {
                            const activeDataSources = await dataSourcesPromise,
                                coreSrc = activeDataSources[0],
                                requiredIndicators = findIndicatorRefs(report.design),
                                isExplorerDriven =
                                    report.design.sections
                                        .map((s: any) => s.widgets)
                                        .flat()
                                        .find((w: any) => w.scriptClass === 'ExplorerWidget') !== undefined,
                                // Finer-grained control over the fetch implementation...
                                fetchInitArgs = {
                                    signal:
                                        abortController !== undefined && abortController !== null
                                            ? abortController.signal
                                            : new AbortController().signal,
                                    cache: 'default'
                                };
                            let idf: string | undefined = report.design?.idField,
                                nmf: string | undefined = report.design?.nameField,
                                outf = [idf, nmf];
                            if (
                                !isNullOrUndefined(report.design?.customSettings) &&
                                !isNullOrUndefined(report.design?.customSettings['dataCachingPolicy']) &&
                                report.design.customSettings['dataCachingPolicy'].toLowerCase() === 'disabled'
                            ) {
                                fetchInitArgs.cache = 'no-cache'; // *default, no-cache, reload, force-cache, only-if-cached
                            }
                            const coreFeatureUrl = coreSrc.url;
                            catalogSrc = activeDataSources.find((s: any) => s.type === 'DataCatalog');
                            if (adjustedByQueryString && catalogSrc !== undefined) {
                                // Secondary check - are the ID and name fields valid?
                                const {
                                    info: fsDetail,
                                    idField: foundIdf,
                                    nameField: foundNmf
                                } = await findStandardFields(
                                    coreFeatureUrl,
                                    idf,
                                    nmf,
                                    undefined,
                                    undefined,
                                    portalUrl,
                                    getTokenFor(coreFeatureUrl, token && token !== '' ? token : undefined)
                                );
                                idf = foundIdf;
                                nmf = foundNmf;
                                outf = [idf, nmf];
                                report.design.idField = foundIdf; // In-memory but may be used elsewhere
                                report.design.nameField = foundNmf;
                                if (
                                    fsDetail !== undefined &&
                                    fsDetail !== null &&
                                    fsDetail.relationships !== undefined &&
                                    fsDetail.relationships !== null
                                ) {
                                    setCoreRelationships([...fsDetail.relationships]); // Attached to the report to make it easy to percolate through the stack...
                                    setCoreName(fsDetail.name);
                                }
                                (report as any).adjustedInMemory = true;
                            }
                            // Data provider
                            const anon = token === undefined || token === null || token.length < 10;
                            reportDataSource = new ReportDataSource(
                                activeDataSources,
                                idf,
                                nmf,
                                anon ? undefined : token,
                                portalUrl,
                                isExplorerDriven ? [] : requiredIndicators,
                                webmap,
                                esriId
                            );
                            setCatalogSource(catalogSrc);
                            // TODO - look at using web workers here, to split the data fetching requests up...
                            //this.dataWorker = new WrappedWebWorker(reportDataWebWorker);
                            //this.dataWorker.onmessage = (e) =>
                            //{
                            //    console.log('worker event' + e); // DEBUG
                            //};
                            //this.dataWorker.postMessage({
                            //    action: 'init',
                            //    sources: activeDataSources,
                            //    idField: idf,
                            //    token: token,
                            //    indicators: requiredIndicators
                            //});
                            const urlInfo = await ArcGISPortal.getInfo(coreFeatureUrl, {
                                f: 'json',
                                token: getTokenFor(coreFeatureUrl, token && token !== '' ? token : undefined)
                            });
                            const isFeatureLayerView = urlInfo && urlInfo.isView === true;
                            const idField =
                                urlInfo !== undefined && urlInfo !== null && urlInfo.fields !== undefined
                                    ? urlInfo.fields.find(
                                          (uf: any) => uf.name.toLowerCase() === (idf ?? 'CODE').toLowerCase()
                                      )
                                    : undefined;
                            const isNumericIdf =
                                    idField !== undefined &&
                                    idField.type !== undefined &&
                                    [
                                        'esriFieldTypeInteger',
                                        'esriFieldTypeBigInteger',
                                        'esriFieldTypeSmallInteger',
                                        'esriFieldTypeDouble',
                                        'esriFieldTypeSingle',
                                        'esriFieldTypeOID'
                                    ].indexOf(idField.type) >= 0,
                                whereClause =
                                    mode === 'aggregate'
                                        ? isNumericIdf &&
                                          featureIds.split(',').filter((fid) => /^[0-9]+$/.test(fid)).length ===
                                              featureIds.split(',').length
                                            ? featureIds
                                                  .split(',')
                                                  .map((fid) => `${idf}=${fid}`)
                                                  .join(' OR ')
                                            : `${idf} IN ('${featureIds.split(',').join(`','`)}')`
                                        : featureIds === '___iaFirstFeature'
                                          ? '1=1'
                                          : /^[^0a-zA-Z_][0-9]+$/.test(featureIds) && isNumericIdf
                                            ? `${idf}=${featureIds}` //` OR ${idf}='${featureIds}'`
                                            : `${idf}='${featureIds}'`,
                                featuresRsp = await ArcGISPortal.queryFeatures(
                                    coreFeatureUrl,
                                    whereClause,
                                    mode === 'aggregate' ? 100 : 1,
                                    {
                                        f: 'json',
                                        token: getTokenFor(coreFeatureUrl, token && token !== '' ? token : undefined),
                                        outFields: outf,
                                        orderByFields: nmf
                                    },
                                    true,
                                    fetchInitArgs
                                ),
                                featureArray = featuresRsp.features;
                            if (featureArray === undefined || featureArray === null || featureArray.length < 1) {
                                throw new Error(`No features found in core layer matching ID(s) "${featureIds}"`);
                            }
                            const fidf = featuresRsp.fields.find(
                                (ff: any) => ff.name.toLowerCase() === (idf ?? 'CODE').toLowerCase()
                            )?.name;
                            const fnmf = featuresRsp.fields.find(
                                (ff: any) => ff.name.toLowerCase() === (nmf ?? 'NAME').toLowerCase()
                            )?.name;
                            f = featureArray.map((ff: any) => {
                                return {
                                    id: ff.attributes[fidf].toString(),
                                    name: ff.attributes[fnmf]
                                };
                            });
                            // Feature layer view - we might need to thin out any full-feature selection (e.g. includeAllAreas)
                            if (isFeatureLayerView) {
                                const fullFeatureSet = (
                                    await ArcGISPortal.queryFeatures(
                                        coreFeatureUrl,
                                        '1=1',
                                        undefined,
                                        {
                                            f: 'json',
                                            token: getTokenFor(
                                                coreFeatureUrl,
                                                token && token !== '' ? token : undefined
                                            ),
                                            outFields: [fidf]
                                        },
                                        true,
                                        fetchInitArgs
                                    )
                                ).features;
                                const fullFeatureIds = fullFeatureSet.map((ff: any) => ff.attributes[fidf].toString());
                                setMaxFeatureFilter(fullFeatureIds);
                            } else {
                                setMaxFeatureFilter([]);
                            }
                        } else {
                            console.log('!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!'); // DEBUG
                            console.log(reportItem); // DEBUG
                            throw new Error('Cannot access data sources');
                        }
                        // Got the feature?
                        if (f !== undefined && Array.isArray(f) && f.length > 0) {
                            // Finer-grained control over the fetch implementation...
                            const fetchInitArgs = {
                                signal:
                                    abortController !== undefined && abortController !== null
                                        ? abortController.signal
                                        : new AbortController().signal,
                                cache: 'default'
                            };
                            if (
                                !isNullOrUndefined(report.design.customSettings) &&
                                !isNullOrUndefined(report.design.customSettings['dataCachingPolicy']) &&
                                report.design.customSettings['dataCachingPolicy'].toLowerCase() === 'disabled'
                            ) {
                                fetchInitArgs.cache = 'no-cache'; // *default, no-cache, reload, force-cache, only-if-cached
                                // Disable caching in-memory for calls to catalog services
                                ArcGISPortal.ALLOW_MEMORY_CACHE = false;
                            }
                            const ds: any = await reportDataSource.init(fetchInitArgs);
                            if (ds.feature !== undefined) {
                                f = Array.isArray(ds.feature) ? ds.feature : [ds.feature];
                            }
                            setLoading(false);
                            setActiveFeature(f);
                            // reportDataSource
                            //     .init(fetchInitArgs)
                            //     .then((ds: any) => {
                            //         // 2nd chance for the feature, because this can be lazy-loaded with the data tables for a static JSON file
                            //         if (ds.feature !== undefined) {
                            //             f = Array.isArray(ds.feature) ? ds.feature : [ds.feature];
                            //         }
                            //         setLoading(false);
                            //         setActiveFeature(f);
                            //     })
                            //     .catch((err: any) => {
                            //         console.log(err); // DEBUG
                            //         setLoading(false);
                            //         setActiveFeature([]);
                            //         setError(err);
                            //     });
                        } else {
                            throw new Error(
                                featureIds === '___iaFirstFeature'
                                    ? `Cannot find features in core layer`
                                    : `Cannot find feature(s) matching IDs ${featureIds}`
                            );
                        }
                        setDataProvider(reportDataSource);
                    } catch (featureErr: any) {
                        setError(featureErr); // DEBUG
                        setActiveFeature([]);
                        setLoading(false);
                    }
                })();
            }
        }
    }, [
        reportItem,
        reportExists,
        reportHasChanged,
        featureIds,
        portalUrl,
        dataMode,
        mode,
        staticUrl, // ↓↓↓ Unlikely to change ↓↓↓, but has an impact if they do...
        reportQueryParams,
        reportLayerIdOverride,
        token
    ]);
    return {
        dataSource: dataProvider,
        catalogSource,
        activeFeature,
        error,
        warning,
        loading,
        maxFeatureFilter,
        coreRelationships,
        coreName
    };
};
